import { parseDuration, Duration } from '../parseDuration';

interface Options {
    ttl?: number | Duration;
    useSessionStorage?: boolean;
}

/**
 * A wrapper around the browser's local and session storage.
 * */
class BrowserStorage {
    private localStorage: Storage;
    private sessionStorage: Storage;

    constructor() {
        try {
            this.localStorage = window.localStorage;
            this.sessionStorage = window.sessionStorage;
        } catch (e) {
            console.error('Browser does not support local/session storage: ', e.message);
            const storage: Storage = {
                getItem: () => null,
                setItem: () => null,
                removeItem: () => null,
                clear: () => null,
                key: () => null,
                length: 0,
            };
            this.localStorage = storage;
            this.sessionStorage = storage;
        }
    }

    /** Checks if the item is a stringified object with an expiry property. */
    private isItemWithExpiry(item: string) {
        try {
            const parsedItem = JSON.parse(item);
            return typeof parsedItem === 'object' && parsedItem.expiry;
        } catch {
            return false;
        }
    }

    /**
     * Sets an item in the browser's local or session storage.
     */
    setItem(key: string, value: string, { useSessionStorage = false }: Options = {}) {
        const storage = useSessionStorage ? this.sessionStorage : this.localStorage;
        storage.setItem(key, value);
    }

    /**
     * Gets an item from the browser's local or session storage.
     * If the item has expired, it is removed from the storage.
     */
    getItem(key: string, { useSessionStorage = false }: Options = {}) {
        const storage = useSessionStorage ? this.sessionStorage : this.localStorage;
        const item = storage.getItem(key);
        if (this.isItemWithExpiry(item)) {
            const parsedItem = JSON.parse(item);
            const now = new Date();
            if (now.getTime() > parsedItem.expiry) {
                this.removeItem(key, { useSessionStorage });
                return null;
            }
            return parsedItem.value;
        }
        return item;
    }

    /**
     * Removes an item from the browser's local or session storage.
     */
    removeItem(key: string, { useSessionStorage = false }: Options = {}) {
        const storage = useSessionStorage ? this.sessionStorage : this.localStorage;
        storage.removeItem(key);
    }

    /**
     * Sets an item in the browser's local or session storage with a TTL.
     */
    setItemWithExpiry(key: string, value: string, { ttl, useSessionStorage }: Options) {
        const ttlInMs = parseDuration(ttl);
        const now = new Date();
        const item = {
            value,
            expiry: now.getTime() + ttlInMs,
        };
        this.setItem(key, JSON.stringify(item), { useSessionStorage });
    }
}

export const browserStorage = new BrowserStorage();
